import { ref } from 'vue'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { materialsToDispose } from '@/utils/meshAppearance'
import { MeshBatches } from '@/utils/meshBatches'
import { fetchGlb } from '@/utils/glbDownload'
import { glbCacheKey } from '@/utils/glbCache'
import { DownloadFailed } from '@/utils/downloadFailed'
import { disposeBoundsTrees } from '@/utils/meshPicking'

export type GlbLoaderStatus = 'idle' | 'loading' | 'ready' | 'error'
export type GlbLoaderError = 'downloadFailed' | 'modelFailed'

function disposeSubtree(root: THREE.Object3D) {
  const meshes: THREE.Mesh[] = []
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return
    obj.geometry?.dispose()
    meshes.push(obj)
  })
  const textures = new Set<THREE.Texture>()
  for (const material of materialsToDispose(meshes)) {
    // Variants share their maps, so textures are collected and disposed once rather than per material.
    for (const value of Object.values(material)) {
      if (value instanceof THREE.Texture) textures.add(value)
    }
    material.dispose()
  }
  for (const texture of textures) texture.dispose()
}

export function useGlbLoader(getScene: () => THREE.Scene | null) {
  const status = ref<GlbLoaderStatus>('idle')
  const error = ref<GlbLoaderError | null>(null)
  /** Download completion in 0..1, or null while the size is unknown. */
  const progress = ref<number | null>(null)
  const allMeshes: THREE.Mesh[] = []
  const meshAncestorNames = new Map<THREE.Mesh, string[]>()

  let loadedUrl: string | null = null
  let loadedGroup: THREE.Group | null = null
  let batches: MeshBatches | null = null
  // Distinguishes the load the viewer is currently waiting on from ones a faster step switch replaced.
  let loadGeneration = 0

  function glbUrlPath(url: string): string {
    try {
      return new URL(url).pathname
    } catch {
      return url
    }
  }

  function clearModel() {
    loadedUrl = null
    batches?.dispose()
    batches = null
    allMeshes.length = 0
    meshAncestorNames.clear()
    if (!loadedGroup) return
    getScene()?.remove(loadedGroup)
    disposeBoundsTrees(loadedGroup)
    disposeSubtree(loadedGroup)
    loadedGroup = null
  }

  async function loadGlb(
    url: string,
    opts: {
      onLoaded?: (group: THREE.Group) => void
    } = {},
  ) {
    const scene = getScene()
    if (!scene) return
    if (loadedUrl && glbUrlPath(url) === glbUrlPath(loadedUrl)) return
    const generation = ++loadGeneration
    clearModel()
    status.value = 'loading'
    error.value = null
    progress.value = null

    const loader = new GLTFLoader()
    loader.setMeshoptDecoder(MeshoptDecoder)
    try {
      const buffer = await fetchGlb(url, glbCacheKey(url), ({ loaded, total }) => {
        if (generation !== loadGeneration) return
        progress.value = total ? Math.min(loaded / total, 1) : null
      })
      if (generation !== loadGeneration) return

      const gltf = await loader.parseAsync(buffer, '')
      if (generation !== loadGeneration) {
        disposeSubtree(gltf.scene)
        return
      }
      const group = gltf.scene

      group.traverse((obj) => {
        if (obj.userData.name) obj.name = obj.userData.name
        if (obj instanceof THREE.Mesh) {
          const ancestors: string[] = []
          let cur: THREE.Object3D | null = obj
          while (cur) {
            if (cur.name) ancestors.push(cur.name)
            cur = cur.parent
          }
          allMeshes.push(obj)
          meshAncestorNames.set(obj, ancestors)
        }

        obj.updateMatrix()
        obj.matrixAutoUpdate = false
      })

      loadedUrl = url
      loadedGroup = group
      scene.add(group)
      // World matrices are no longer maintained per frame, so the fresh subtree is placed once here.
      group.updateMatrixWorld(true)
      batches = new MeshBatches(allMeshes)
      scene.add(batches.root)
      progress.value = 1
      opts.onLoaded?.(group)
      status.value = 'ready'
    } catch (e: unknown) {
      if (generation !== loadGeneration) return
      error.value = e instanceof DownloadFailed ? 'downloadFailed' : 'modelFailed'
      status.value = 'error'
    }
  }

  function dispose() {
    loadGeneration++
    clearModel()
  }

  return {
    status,
    error,
    progress,
    allMeshes,
    meshAncestorNames,
    syncBatches: () => batches?.sync(),
    clearModel,
    loadGlb,
    dispose,
  }
}
